import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { parseUtc } from '../lib/calendarDate';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Calendar, Clock, User as UserIcon, CheckCircle2, XCircle, Loader2, Video, Download, Mail, Inbox, Bell, Share2, Copy, Linkedin, MessageCircle, Plus, CalendarPlus, Power, PowerOff, MoreHorizontal, Pencil, Trash2, Bookmark, BookmarkX, Star, MessageSquarePlus, Banknote, Hourglass, TrendingUp, ChevronDown, Heart } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { toast } from 'sonner';
import ExpertCalendar from '../components/ExpertCalendar';
import SessionWizard from '../components/SessionWizard';
import AvailabilityWizard from '../components/AvailabilityWizard';
import EventCreationWizard from '../components/EventCreationWizard';
import ProspectionDashboard from './ProspectionDashboard';
import RevenueTab from '../components/RevenueTab';
import ReviewForm from '../components/ReviewForm';
import SessionDetailsDrawer from '../components/SessionDetailsDrawer';
import SessionRequestsSection from '../components/SessionRequestsSection';
import PaymentSuccessModal from '../components/PaymentSuccessModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_META = {
  // Brief audit beta Feb 2026 (P0 Bug 3) — Wording explicite pour dissiper toute
  // ambiguïté côté apprenant (« Terminé » était mal interprété).
  pending: { label: 'En attente de validation', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  pending_payment: { label: 'Paiement en attente', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  confirmed: { label: 'Confirmée', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  completed: { label: 'Terminée', cls: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200' },
  cancelled: { label: 'Annulée', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  declined: { label: 'Refusée', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
};

// Brief Vague 2 §2 — Badge rôle contextuel sur chaque item de la liste (remplace
// les onglets de rôle séparés). Couleurs accent Home v3.
const ROLE_PILL = (role) => {
  if (role === 'expert') {
    return { label: 'Expert', style: { background: '#EEEDFE', color: '#3C3489' } };
  }
  if (role === 'client') {
    return { label: 'Apprenant', style: { background: '#E1F5EE', color: '#0F6E56' } };
  }
  if (role === 'invited') {
    return { label: 'Invitation reçue', style: { background: '#FDE5C8', color: '#7A3E08' } };
  }
  if (role === 'inviter') {
    return { label: 'Invitation envoyée', style: { background: '#F3F4F6', color: '#374151' } };
  }
  // Brief Phase B2 §4 — bookmark badge
  if (role === 'bookmarked') {
    return { label: 'Suivi', style: { background: '#FDE5C8', color: '#7A3E08' } };
  }
  return { label: role || '—', style: { background: '#F3F4F6', color: '#374151' } };
};

const parseStart = (row) => {
  if (!row?.start_time) return null;
  if (typeof row.start_time === 'string') {
    // Support both ISO and bookings flat-string (HH:MM); rows from /me/all-sessions
    // for bookings carry only HH:MM (start_time) + a date in payload — fall back gracefully.
    // Fix timezone Jul 2026 — backend datetimes are naive UTC: parseUtc, jamais new Date.
    if (row.start_time.includes('T')) return parseUtc(row.start_time);
    if (row.date) return parseUtc(`${row.date}T${row.start_time}:00`);
    return null;
  }
  return parseUtc(row.start_time);
};

const fmtDateFR = (d) => {
  if (!d) return '';
  try {
    return d.toLocaleDateString('fr-CH', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return ''; }
};
const fmtTimeFR = (d) => {
  if (!d) return '';
  try {
    return d.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

// Brief Aug 10 2026 — badge "🔴 En cours" : session active maintenant.
const isLiveNow = (row) => {
  if (['cancelled', 'completed', 'declined', 'draft', 'pending', 'pending_payment'].includes(row.status)) return false;
  const dt = parseStart(row);
  if (!dt) return false;
  const end = new Date(dt.getTime() + (Number(row.duration) || 60) * 60000);
  const n = new Date();
  return n >= dt && n <= end;
};

const canJoinNow = (row) => {
  const start = parseStart(row);
  if (!start) return false;
  const end = new Date(start.getTime() + (row.duration || 60) * 60000 + 60 * 60000);
  const activeFrom = new Date(start.getTime() - 15 * 60000);
  const now = new Date();
  return now >= activeFrom && now <= end;
};

const SessionCard = ({ row, onAction, shareOpenFor, setShareOpenFor, publicEventBaseUrl, onEditEvent, onDeleteEvent, onUnbookmark, ownerMenuOpenFor, setOwnerMenuOpenFor, onLeaveReview, onOpenDetails, onPublishEvent, onPayBooking, highlightId }) => {
  const start = parseStart(row);
  const cp = row.counterpart || {};
  const rolePill = ROLE_PILL(row.viewer_role);
  const statusMeta = STATUS_META[row.status] || { label: row.status || '—', cls: 'bg-gray-100 text-gray-700' };
  const isBooking = row.kind === 'booking';
  const isInvitation = row.kind === 'invitation';
  // Brief Jun 17 §4 — owned public events without bookings : the expert sees
  // them in the list with a "0 inscrit" hint and the same share toolbar as a
  // booking row.
  const isOwnedEvent = row.kind === 'owned_event';
  // Brief Phase B2 §4 — bookmarked public event ("Suivi", no seat reserved)
  const isBookmarkedEvent = row.kind === 'bookmarked_event';
  // Brief Jun 15 (post Vague 2) §4 — share button visible on owned public
  // events (slot/event/series) — bookings as expert OR owned_event rows.
  const ownedPublicEventType = ['public_event', 'public_slot', 'public_series'];
  const isShareable = (
    (isBooking && row.viewer_role === 'expert' && row.event_id
      && row.event_type && ownedPublicEventType.includes(row.event_type))
    || (isOwnedEvent && row.event_id && row.status !== 'draft'
      && row.type && ownedPublicEventType.includes(row.type))
  );
  const shareUrl = isShareable
    ? (row.slug && row.expert_handle
        ? `${publicEventBaseUrl}/api/@${row.expert_handle}/e/${row.slug}`
        : `${publicEventBaseUrl}/event/${row.event_id}`)
    : '';
  const shareMessage = isShareable
    ? `Je propose une session : ${row.session_type?.title || row.title || 'Session'}`
    : '';
  const isShareOpen = shareOpenFor === row.event_id;
  const isOwnerMenuOpen = ownerMenuOpenFor === row.event_id;
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Lien copié !');
    } catch {
      toast.error('Impossible de copier le lien');
    }
    setShareOpenFor && setShareOpenFor(null);
  };

  return (
    <div
      data-testid={`session-row-${row.booking_id || row.event_id}`}
      data-kind={row.kind}
      data-viewer-role={row.viewer_role}
      className={`flex flex-col sm:flex-row sm:items-center gap-4 p-4 rounded-2xl border bg-white dark:bg-gray-800 hover:shadow-md transition-all ${
        highlightId && highlightId === String(row.booking_id || row.event_id)
          ? 'border-[#7F77DD] ring-2 ring-[#7F77DD]/60 shadow-lg'
          : 'border-gray-200/70 dark:border-gray-700/70'
      }`}
    >
      <Avatar className="h-12 w-12 shrink-0">
        <AvatarImage src={cp.avatar} />
        <AvatarFallback className="bg-gradient-to-br from-cyan-500 to-pink-500 text-white text-sm">
          {(isOwnedEvent || isBookmarkedEvent) ? <Calendar className="h-5 w-5" /> : (cp.display_name || '?').split(' ').map(n => n[0]).join('').substring(0, 2)}
        </AvatarFallback>
      </Avatar>
      {/* Audit Fable5 Jul 7 2026 (Bug 7) — la zone d'info est cliquable et
          ouvre le panneau de détails (drawer) */}
      <div
        className="flex-1 min-w-0 cursor-pointer"
        role="button"
        tabIndex={0}
        onClick={() => onOpenDetails && onOpenDetails(row)}
        onKeyDown={(e) => { if (e.key === 'Enter' && onOpenDetails) onOpenDetails(row); }}
        data-testid={`session-card-open-${row.booking_id || row.event_id}`}
      >
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-semibold text-gray-900 dark:text-white truncate">
            {(isOwnedEvent || isBookmarkedEvent) ? (row.title || 'Session sans titre') : (cp.display_name || 'Utilisateur')}
          </p>
          <span
            className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
            style={rolePill.style}
            data-testid={`session-role-badge-${row.viewer_role}`}
          >
            {rolePill.label}
          </span>
          {!isOwnedEvent && !isBookmarkedEvent && (
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusMeta.cls}`}>{statusMeta.label}</span>
          )}
          {/* Brief Jul 6 2026 P1 — badge "Payé" côté apprenant sur booking payant confirmé */}
          {isBooking && row.viewer_role === 'client' && Number(row.price) > 0 && !row.is_free && row.status === 'confirmed' && (
            <span
              className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
              data-testid={`session-paid-badge-${row.booking_id}`}
            >
              <Banknote className="h-3 w-3" /> Payé · {row.price} CHF
            </span>
          )}
          {isLiveNow(row) && (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" data-testid={`session-live-badge-${row.booking_id || row.event_id}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> En cours
            </span>
          )}
          {isOwnedEvent && (
            row.status === 'draft' ? (
              <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" data-testid={`session-draft-badge-${row.event_id}`}>
                Brouillon
              </span>
            ) : (
              <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300" data-testid={`session-owned-badge-${row.event_id}`}>
                Publié
              </span>
            )
          )}
          {isBookmarkedEvent && (
            <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" data-testid={`session-bookmark-badge-${row.event_id}`}>
              <Bookmark className="inline h-3 w-3 mr-0.5" />Bookmark
            </span>
          )}
          {/* Brief Phase B3 §2 — invitation envoyée à un email externe (hors OS) */}
          {isInvitation && row.is_external_invitation && (
            <span
              className="text-xs px-2 py-0.5 rounded-full font-medium bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300"
              data-testid={`session-external-invitation-badge-${row.event_id}`}
              title={row.invited_email || ''}
            >
              <Mail className="inline h-3 w-3 mr-0.5" />Invitation externe
            </span>
          )}
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300 mt-0.5 truncate">
          {isInvitation
            ? (row.title || 'Session 1-to-1')
            : isOwnedEvent
              ? (row.description || `Session ${row.type === 'public_event' ? 'publique' : row.type === 'public_series' ? 'récurrente' : 'créneau'}`)
              : isBookmarkedEvent
                ? (cp.display_name ? `Par ${cp.display_name}` : (row.description || 'Événement suivi'))
                : (row.session_type?.title || row.title || 'Session')
          }
        </p>
        {/* Brief Phase B3 §2 — afficher l'email externe en sous-titre */}
        {isInvitation && row.is_external_invitation && row.invited_email && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate" data-testid={`session-external-invitation-email-${row.event_id}`}>
            Envoyée à {row.invited_email}
          </p>
        )}
        {/* Brief Vague 2 §4 — Affiche le nombre d'inscrits pour les events publics owned */}
        {((isBooking && row.viewer_role === 'expert') || isOwnedEvent) && row.event_id && typeof row.participants_count === 'number' && (
          <p className="text-xs mt-0.5 font-medium" style={{ color: row.participants_count > 0 ? '#7F77DD' : '#9CA3AF' }} data-testid={`session-participants-${row.booking_id || row.event_id}`}>
            {row.participants_count > 0
              ? `${row.participants_count} inscrit${row.participants_count > 1 ? 's' : ''}`
              : 'Aucun inscrit pour l\u2019instant'
            }
          </p>
        )}
        {/* Brief Jul 6 2026 P1 — badges paiement + revenus côté expert */}
        {((isBooking && row.viewer_role === 'expert') || isOwnedEvent) && (() => {
          const ps = row.payment_stats || {};
          const hasMoney = (ps.paid_count > 0) || (ps.pending_payment_count > 0);
          const isFreeRow = (row.is_free || !(Number(row.price) > 0)) && !hasMoney;
          return (
            <div className="flex flex-wrap items-center gap-1.5 mt-1" data-testid={`session-payment-info-${row.booking_id || row.event_id}`}>
              {isFreeRow ? (
                <span
                  className="text-[11px] px-2 py-0.5 rounded-full font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                  data-testid={`session-payment-free-${row.booking_id || row.event_id}`}
                >
                  Gratuit
                </span>
              ) : (
                <>
                  {ps.paid_count > 0 && (
                    <span
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                      data-testid={`session-payment-paid-${row.booking_id || row.event_id}`}
                    >
                      <Banknote className="h-3 w-3" /> {ps.paid_count} payé{ps.paid_count > 1 ? 's' : ''} · {ps.revenue_paid} CHF encaissés
                    </span>
                  )}
                  {ps.pending_payment_count > 0 && (
                    <span
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                      data-testid={`session-payment-pending-${row.booking_id || row.event_id}`}
                    >
                      <Hourglass className="h-3 w-3" /> {ps.pending_payment_count} paiement{ps.pending_payment_count > 1 ? 's' : ''} en attente ({ps.revenue_pending} CHF)
                    </span>
                  )}
                  {!hasMoney && (
                    <span
                      className="text-[11px] px-2 py-0.5 rounded-full font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
                      data-testid={`session-payment-price-${row.booking_id || row.event_id}`}
                    >
                      Payant · {row.price} CHF
                    </span>
                  )}
                </>
              )}
            </div>
          );
        })()}
        <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mt-1 flex-wrap">
          <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{fmtDateFR(start)}</span>
          <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{fmtTimeFR(start)}</span>
          {row.duration && <span>{row.duration} min</span>}
          {row.price ? <span className="font-semibold text-gray-700 dark:text-gray-300">{row.price} CHF</span> : null}
        </div>
      </div>
      {/* Expert actions on pending bookings */}
      {isBooking && row.viewer_role === 'expert' && row.status === 'pending' && (
        <div className="flex gap-2">
          <button
            data-testid={`session-accept-${row.booking_id}`}
            onClick={() => onAction('booking', row.booking_id, 'confirmed')}
            className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm rounded-lg font-medium inline-flex items-center gap-1"
          >
            <CheckCircle2 className="h-4 w-4" /> Accepter
          </button>
          <button
            data-testid={`session-decline-${row.booking_id}`}
            onClick={() => onAction('booking', row.booking_id, 'cancelled')}
            className="px-3 py-1.5 bg-gray-100 hover:bg-red-50 dark:bg-gray-700 dark:hover:bg-red-900/30 text-red-600 text-sm rounded-lg font-medium inline-flex items-center gap-1"
          >
            <XCircle className="h-4 w-4" /> Refuser
          </button>
        </div>
      )}
      {/* Invitation actions when I'm the invited party */}
      {isInvitation && row.viewer_role === 'invited' && row.status === 'pending' && (
        <div className="flex gap-2">
          <button
            data-testid={`invitation-accept-${row.event_id}`}
            onClick={() => onAction('invitation', row.event_id, 'accept')}
            className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm rounded-lg font-medium inline-flex items-center gap-1"
          >
            <CheckCircle2 className="h-4 w-4" /> Accepter l&apos;invitation
          </button>
          <button
            data-testid={`invitation-decline-${row.event_id}`}
            onClick={() => onAction('invitation', row.event_id, 'decline')}
            className="px-3 py-1.5 bg-gray-100 hover:bg-red-50 dark:bg-gray-700 dark:hover:bg-red-900/30 text-red-600 text-sm rounded-lg font-medium inline-flex items-center gap-1"
          >
            <XCircle className="h-4 w-4" /> Décliner
          </button>
        </div>
      )}
      {/* Join visio for confirmed bookings */}
      {isBooking && row.status === 'confirmed' && row.visio_link && (
        <div className="flex gap-2 flex-wrap">
          <a
            data-testid={`session-join-${row.booking_id}`}
            href={row.visio_link}
            target="_blank"
            rel="noreferrer"
            onClick={() => {
              // Brief Jul 27 2026 §1 — l'expert qui rejoint notifie les
              // participants (one-shot, fenêtre validée côté backend).
              if (row.viewer_role === 'expert' && row.event_id) {
                axios.post(`${API}/calendar/events/${row.event_id}/session-started`, {}, { withCredentials: true }).catch(() => {});
              }
            }}
            className={`px-3 py-1.5 text-sm rounded-lg font-medium inline-flex items-center gap-1 ${
              canJoinNow(row)
                ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white animate-pulse'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200'
            }`}
            title={canJoinNow(row) ? 'La session est active' : 'Actif 15 min avant l\'heure'}
          >
            <Video className="h-4 w-4" /> Rejoindre
          </a>
          <a
            data-testid={`session-ics-${row.booking_id}`}
            href={`${BACKEND_URL}/api/bookings/${row.booking_id}/ical`}
            className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm rounded-lg font-medium inline-flex items-center gap-1"
            title="Ajouter au calendrier"
          >
            <Download className="h-4 w-4" />
          </a>
        </div>
      )}

      {/* Brief Phase B3 §1 — "Laisser un avis" / "Avis laissé" sur les bookings
          côté apprenant (client), session passée < 30 jours. Le backend nous
          envoie `is_reviewable` (true si autorisé) et `has_review` (déjà fait). */}
      {isBooking && row.viewer_role === 'client' && row.has_review && (
        <span
          className="px-3 py-1.5 text-sm rounded-lg font-medium inline-flex items-center gap-1 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/40"
          data-testid={`session-review-submitted-${row.booking_id}`}
        >
          <Star className="h-3.5 w-3.5 fill-current" /> Avis laissé
        </span>
      )}
      {isBooking && row.viewer_role === 'client' && row.is_reviewable && !row.has_review && (
        <button
          type="button"
          onClick={() => onLeaveReview && onLeaveReview(row)}
          className="px-3 py-1.5 text-sm rounded-lg font-medium inline-flex items-center gap-1.5 bg-gradient-to-r from-amber-400 to-yellow-500 text-white hover:from-amber-500 hover:to-yellow-600 transition-colors shadow-sm"
          data-testid={`session-leave-review-btn-${row.booking_id}`}
        >
          <MessageSquarePlus className="h-3.5 w-3.5" /> Laisser un avis
        </button>
      )}

      {/* Brief pré-beta (Task 1) — publier un brouillon depuis la liste */}
      {isOwnedEvent && row.status === 'draft' && (
        <button
          type="button"
          onClick={() => onPublishEvent && onPublishEvent(row)}
          className="px-3 py-1.5 bg-[#7F77DD] hover:opacity-90 text-white text-sm rounded-lg font-medium inline-flex items-center gap-1"
          data-testid={`session-publish-${row.event_id}`}
        >
          <CheckCircle2 className="h-4 w-4" /> Publier
        </button>
      )}

      {/* Brief pré-beta (Task 4) — payer une réservation en attente de paiement */}
      {isBooking && row.viewer_role === 'client' && row.status === 'pending_payment' && Number(row.price) > 0 && (
        <button
          type="button"
          onClick={() => onPayBooking && onPayBooking(row)}
          className="px-3 py-1.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm rounded-lg font-medium inline-flex items-center gap-1"
          data-testid={`session-pay-${row.booking_id}`}
        >
          <Banknote className="h-4 w-4" /> Payer {row.price} CHF
        </button>
      )}

      {/* Brief Phase B2 §2 — Owner menu (Modifier / Supprimer) sur les sessions
          que je possède dans la liste — sans avoir à passer par le calendrier. */}
      {isOwnedEvent && row.event_id && (
        <div className="relative">
          <button
            type="button"
            onClick={() => setOwnerMenuOpenFor(isOwnerMenuOpen ? null : row.event_id)}
            onBlur={() => setTimeout(() => setOwnerMenuOpenFor(prev => prev === row.event_id ? null : prev), 200)}
            className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
            data-testid={`session-owner-menu-toggle-${row.event_id}`}
            aria-label="Plus d'actions"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
          {isOwnerMenuOpen && (
            <div
              className="absolute right-0 top-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 z-20 min-w-[160px]"
              data-testid={`session-owner-menu-${row.event_id}`}
            >
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); onEditEvent && onEditEvent(row); setOwnerMenuOpenFor(null); }}
                className="w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                data-testid={`session-owner-edit-${row.event_id}`}
              >
                <Pencil className="h-3.5 w-3.5 text-[#1D9E75]" /> Modifier
              </button>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); onDeleteEvent && onDeleteEvent(row); setOwnerMenuOpenFor(null); }}
                className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center gap-2"
                data-testid={`session-owner-delete-${row.event_id}`}
              >
                <Trash2 className="h-3.5 w-3.5" /> Supprimer
              </button>
            </div>
          )}
        </div>
      )}

      {/* Brief Phase B2 §4 — bookmarked event : bouton "Ne plus suivre" */}
      {isBookmarkedEvent && row.event_id && (
        <button
          type="button"
          onClick={() => onUnbookmark && onUnbookmark(row.event_id)}
          className="px-3 py-1.5 text-sm rounded-lg font-medium inline-flex items-center gap-1.5 text-gray-500 hover:text-red-600 border border-gray-200 dark:border-gray-700 hover:border-red-300 transition-colors"
          data-testid={`session-bookmark-remove-${row.event_id}`}
        >
          <BookmarkX className="h-4 w-4" /> Ne plus suivre
        </button>
      )}

      {/* Brief Jun 15 (post Vague 2) §4 — share button on owned public events. */}
      {isShareable && (
        <div className="relative">
          <button
            type="button"
            onClick={() => setShareOpenFor(isShareOpen ? null : row.event_id)}
            onBlur={() => setTimeout(() => setShareOpenFor(prev => prev === row.event_id ? null : prev), 200)}
            className="px-3 py-1.5 text-sm rounded-lg font-medium inline-flex items-center gap-1.5 transition-opacity hover:opacity-80"
            style={{
              background: 'transparent',
              color: 'var(--color-text-secondary)',
              border: '1px solid var(--color-border-tertiary)',
              borderRadius: 'var(--border-radius-md)',
            }}
            data-testid={`session-share-toggle-${row.event_id}`}
            aria-label="Partager l'événement"
          >
            <Share2 className="h-4 w-4" /> Partager
          </button>
          {isShareOpen && (
            <div
              className="absolute right-0 top-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 z-10 min-w-[180px]"
              data-testid={`session-share-menu-${row.event_id}`}
            >
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setShareOpenFor(null)}
                className="w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                data-testid={`session-share-linkedin-${row.event_id}`}
              >
                <Linkedin className="h-3.5 w-3.5 text-[#0A66C2]" /> LinkedIn
              </a>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`${shareMessage} → ${shareUrl}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setShareOpenFor(null)}
                className="w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                data-testid={`session-share-whatsapp-${row.event_id}`}
              >
                <MessageCircle className="h-3.5 w-3.5 text-[#25D366]" /> WhatsApp
              </a>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); handleCopyLink(); }}
                className="w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                data-testid={`session-share-copy-${row.event_id}`}
              >
                <Copy className="h-3.5 w-3.5" /> Copier le lien
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Brief Sep 2026 — le mini-widget revenus (iter_80) a été remplacé par
// l'onglet « Revenus » (composant RevenueTab), sur demande du fondateur.

/* Brief 27 oct LOT E.2 — section repliable : 3 éléments par défaut, « Voir plus »
   ou clic sur le titre pour déplier. Se déplie seule si l'élément à mettre en
   évidence (?highlight=) est au-delà des 3 premiers. */
const SECTION_PREVIEW = 3;
const SessionSection = ({ testId, title, rows, renderRow, highlightId, defaultExpanded = false, empty = null, dimmed = false }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const hiddenHasHighlight = !!highlightId && rows.slice(SECTION_PREVIEW).some((r) => String(r.booking_id || r.event_id) === String(highlightId));
  const open = expanded || hiddenHasHighlight;
  const visible = open ? rows : rows.slice(0, SECTION_PREVIEW);
  const hidden = rows.length - visible.length;
  const collapsible = rows.length > SECTION_PREVIEW;
  return (
    <section data-testid={testId}>
      <button
        type="button"
        onClick={() => collapsible && setExpanded((v) => !v)}
        className={`w-full flex items-center justify-between mb-3 text-left ${collapsible ? 'cursor-pointer group' : 'cursor-default'}`}
        data-testid={`${testId}-title`}
        aria-expanded={open}
      >
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-300 transition-colors">
          {title} <span className="text-gray-400 font-normal">({rows.length})</span>
        </h2>
        {collapsible && (
          <span className="text-xs font-medium text-purple-600 dark:text-purple-300 inline-flex items-center gap-1">
            {open ? 'Réduire' : `Voir tout (${rows.length})`}
            <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
          </span>
        )}
      </button>
      {rows.length === 0 ? empty : (
        <div className={`space-y-3 ${dimmed ? 'opacity-80' : ''}`}>
          {visible.map(renderRow)}
          {hidden > 0 && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="w-full py-2.5 rounded-xl border border-dashed border-gray-300 dark:border-gray-700 text-sm font-medium text-purple-600 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors"
              data-testid={`${testId}-more`}
            >
              Voir plus ({hidden} autre{hidden > 1 ? 's' : ''})
            </button>
          )}
        </div>
      )}
    </section>
  );
};

/* Brief 27 oct LOT E.3 — personnes qui ont cliqué « Je suis intéressé·e » (côté expert). */
const InterestedSection = ({ filterActive }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/me/expert-interests`, { withCredentials: true })
      .then((r) => setRows(r.data.interests || [])).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (filterActive && rows?.length) {
      setTimeout(() => document.querySelector('[data-testid="mysessions-interested-section"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 200);
    }
  }, [filterActive, rows]);
  const handle = async (row) => {
    try {
      await axios.post(`${API}/me/expert-interests/${row.interest_id}/handle`, {}, { withCredentials: true });
      setRows((prev) => prev.filter((r) => r.interest_id !== row.interest_id));
      toast.success('Marqué comme traité');
    } catch { toast.error("L'action n'a pas pu aboutir"); }
  };
  if (!rows || rows.length === 0) return null;
  return (
    <section className={`rounded-2xl border p-4 ${filterActive ? 'border-rose-300 dark:border-rose-700 ring-2 ring-rose-200 dark:ring-rose-900/40' : 'border-rose-200 dark:border-rose-900/50'} bg-rose-50/60 dark:bg-rose-900/10`} data-testid="mysessions-interested-section">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Heart className="h-4 w-4 text-rose-500" /> Personnes intéressées <span className="text-gray-400 font-normal">({rows.length})</span>
        </h2>
        <p className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">Elles attendent un créneau : propose-leur une session.</p>
      </div>
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.interest_id} className="flex items-center gap-3 p-3 rounded-xl bg-white dark:bg-gray-800 border border-rose-100 dark:border-gray-700" data-testid={`interest-row-${r.interest_id}`}>
            {r.avatar ? <img src={r.avatar} alt="" className="h-10 w-10 rounded-full object-cover" /> : <div className="h-10 w-10 rounded-full bg-gradient-to-br from-rose-400 to-pink-500 text-white flex items-center justify-center text-sm font-bold">{(r.display_name || '?').substring(0, 2).toUpperCase()}</div>}
            <div className="flex-1 min-w-0">
              <button type="button" onClick={() => navigate(`/app/profile/${r.user_id}`)} className="font-medium text-gray-900 dark:text-white hover:text-purple-600 truncate block text-left">{r.display_name}</button>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{r.message ? `« ${r.message} »` : 'Souhaite une session avec toi'} · {new Date(r.created_at).toLocaleDateString('fr-CH', { day: 'numeric', month: 'short' })}</p>
            </div>
            <button type="button" onClick={() => navigate(`/app/messages/${r.user_id}`)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:opacity-90" data-testid={`interest-propose-${r.interest_id}`}>
              <MessageCircle className="h-3.5 w-3.5" /> Proposer un créneau
            </button>
            <button type="button" onClick={() => handle(r)} className="p-1.5 rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20" title="Marquer comme traité" data-testid={`interest-handle-${r.interest_id}`}>
              <CheckCircle2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </section>
  );
};


const MySessions = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [legacyBookings, setLegacyBookings] = useState({ as_client: [], as_expert: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // Brief Vague 2 §2 — 'all' | 'invitation' (rôles supprimés)
  // Brief Jun 15 (post Vague 2) §3 — when arriving from the bell or sidebar
  // badge (?filter=pending), scroll to the En attente section + flash the banner.
  const [searchParams, setSearchParams] = useSearchParams();
  // Brief Jul 10 2026 — onglets internes [Sessions] [Prospection].
  // Brief Sep 2026 — + onglet [Revenus] (deep-link ?tab=revenus).
  const tabParam = searchParams.get('tab');
  const activeTab = tabParam === 'prospection' ? 'prospection' : tabParam === 'revenus' ? 'revenus' : 'sessions';
  const switchTab = useCallback((tab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === 'prospection' || tab === 'revenus') next.set('tab', tab);
      else next.delete('tab');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [unifiedRes, legacyRes] = await Promise.all([
        axios.get(`${API}/me/all-sessions`, { withCredentials: true }),
        // Legacy /bookings hydration keeps session_type + visio_link details that the
        // unified endpoint doesn't compute (would be costly). We merge them in.
        axios.get(`${API}/bookings`, { withCredentials: true }).catch(() => ({ data: {} })),
      ]);
      setSessions(unifiedRes.data.sessions || []);
      const lb = legacyRes.data || {};
      setLegacyBookings({ as_client: lb.as_client || [], as_expert: lb.as_expert || [] });
    } catch (e) {
      if (!silent) toast.error('Impossible de charger vos sessions');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { if (user?.user_id) load(); }, [user?.user_id, load]);

  // Brief Aug 10 2026 — rafraîchissement silencieux (60 s) : le passage
  // "À venir → 🔴 En cours → Passée" est visible sans recharger la page.
  useEffect(() => {
    if (!user?.user_id) return undefined;
    const id = setInterval(() => load(true), 60000);
    return () => clearInterval(id);
  }, [user?.user_id, load]);

  // Build a lookup of legacy bookings to enrich unified rows (session_type + visio_link)
  const legacyById = useMemo(() => {
    const map = {};
    [...legacyBookings.as_client, ...legacyBookings.as_expert].forEach(b => {
      if (b.booking_id) map[b.booking_id] = b;
    });
    return map;
  }, [legacyBookings]);

  const enrichedSessions = useMemo(() => {
    return sessions.map(s => {
      if (s.kind === 'booking' && s.booking_id && legacyById[s.booking_id]) {
        const lb = legacyById[s.booking_id];
        return {
          ...s,
          date: lb.date || s.date,
          session_type: lb.session_type || s.session_type,
          // Audit Fable5 Jul 7 2026 — ne jamais écraser le visio_link hydraté
          // par le backend (my_sessions) avec un null legacy.
          visio_link: lb.visio_link || s.visio_link,
          // prefer the legacy start_time (HH:MM) joined with date so the row renders correctly
          start_time: s.start_time || lb.start_time,
        };
      }
      return s;
    });
  }, [sessions, legacyById]);

  const handleAction = useCallback(async (kind, id, action) => {
    try {
      if (kind === 'booking') {
        await axios.put(`${API}/bookings/${id}/status?status=${encodeURIComponent(action)}`, null, { withCredentials: true });
        toast.success(action === 'confirmed' ? 'Session confirmée' : 'Session refusée');
      } else if (kind === 'invitation') {
        await axios.post(`${API}/calendar/events/${id}/respond-invitation`, { accept: action === 'accept' }, { withCredentials: true });
        toast.success(action === 'accept' ? 'Invitation acceptée' : 'Invitation déclinée');
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action impossible');
    }
  }, [load]);

  const now = new Date();
  const filtered = enrichedSessions.filter(row => {
    if (filter === 'all') return true;
    if (filter === 'invitation') return row.kind === 'invitation';
    return true;
  });

  // Brief Jul 27 2026 §3 — une session EN COURS reste dans "À venir" (bouton
  // Rejoindre actif) : le split À venir/Passées se fait sur l'heure de FIN
  // (début + durée), jamais sur l'heure de début.
  const endOfRow = (r) => {
    const dt = parseStart(r);
    return dt ? new Date(dt.getTime() + (Number(r.duration) || 60) * 60000) : null;
  };
  const pending = filtered.filter(r => r.status === 'pending' || r.status === 'pending_payment');
  const upcoming = filtered.filter(r => {
    const end = endOfRow(r);
    return end && end >= now
      && r.status !== 'cancelled'
      && r.status !== 'completed'
      && r.status !== 'pending'
      && r.status !== 'pending_payment'
      && r.status !== 'declined';
  });
  const past = filtered.filter(r => {
    const end = endOfRow(r);
    return (end && end < now && r.status !== 'pending' && r.status !== 'pending_payment') || r.status === 'completed' || r.status === 'cancelled' || r.status === 'declined';
  });

  // Brief Jun 15 (post Vague 2) §3 — pendingInvitations count for the banner.
  // Includes both pending bookings where I'm the expert AND private 1on1
  // invitations awaiting my response.
  const pendingInvitations = useMemo(() => enrichedSessions.filter(r => {
    if (r.kind === 'invitation') return true;
    return r.kind === 'booking' && r.viewer_role === 'expert' && (r.status === 'pending' || r.status === 'pending_payment');
  }).length, [enrichedSessions]);

  // ?filter=pending → highlight the En attente section by switching to "Tout"
  // and scrolling to the pending list once data is loaded.
  useEffect(() => {
    if (searchParams.get('filter') === 'pending' && !loading && pendingInvitations > 0) {
      const t = setTimeout(() => {
        const el = document.querySelector('[data-testid="mysessions-pending-section"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 250);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [searchParams, loading, pendingInvitations]);

  const counts = useMemo(() => ({
    all: enrichedSessions.length,
    invitation: enrichedSessions.filter(r => r.kind === 'invitation').length,
  }), [enrichedSessions]);

  // Brief Jun 15 (post Vague 2) §4 — share menu state for owned public events.
  const [shareOpenFor, setShareOpenFor] = useState(null);
  // Brief Phase B2 §2 — owner ... menu state (only one open at a time).
  const [ownerMenuOpenFor, setOwnerMenuOpenFor] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);  // Brief Phase B2 §2
  const [deletingRow, setDeletingRow] = useState(null);    // Brief Phase B2 §2 (confirm)
  // Brief Phase B3 §1 — review modal state (single row at a time).
  const [reviewingRow, setReviewingRow] = useState(null);
  // Audit Fable5 Jul 7 2026 (Bug 7) — drawer de détails de session.
  const [detailsRow, setDetailsRow] = useState(null);
  // Brief Jul 7 2026 §1 — feedback post-paiement Stripe.
  const [paymentSuccess, setPaymentSuccess] = useState(null);
  useEffect(() => {
    const bookingParam = searchParams.get('booking');
    const evId = searchParams.get('event_id');
    if (!bookingParam || !evId) return;
    const next = new URLSearchParams(searchParams);
    next.delete('booking'); next.delete('event_id'); next.delete('session_id');
    setSearchParams(next, { replace: true });
    if (bookingParam === 'success') {
      axios.get(`${API}/bookings/${evId}/payment-status`, { withCredentials: true })
        .then(({ data }) => {
          if (data.booking_status === 'confirmed' || data.payment_status === 'paid') {
            setPaymentSuccess({ ...(data.event || {}), booking_id: data.booking_id });
            toast.success(`Réservation confirmée · ${data.event?.title || 'ta session'}`, { duration: Infinity, closeButton: true });
          } else {
            toast.info('Paiement en cours de confirmation… La session apparaîtra dès validation.', { duration: 10000 });
          }
          load();
        })
        .catch(() => toast.error('Impossible de vérifier le statut du paiement.'));
    } else if (bookingParam === 'failed') {
      toast.error('❌ Le paiement a échoué. Vérifie ta carte et réessaie.', {
        duration: 20000,
        closeButton: true,
        action: {
          label: 'Réessayer',
          onClick: async () => {
            try {
              const { data } = await axios.post(`${API}/bookings/${evId}/checkout`, {}, { withCredentials: true });
              if (data.url) window.location.href = data.url;
            } catch {
              toast.error('Impossible de relancer le paiement.');
            }
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  // Audit Fable5 Jul 7 2026 (Bug 6) — ?highlight={id} : scroll + ring sur la card.
  const [highlightId, setHighlightId] = useState(null);
  useEffect(() => {
    const hl = searchParams.get('highlight');
    if (hl && !loading) {
      setHighlightId(hl);
      const t = setTimeout(() => {
        const el = document.querySelector(`[data-testid="session-row-${hl}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
      const t2 = setTimeout(() => setHighlightId(null), 4000);
      return () => { clearTimeout(t); clearTimeout(t2); };
    }
    return undefined;
  }, [searchParams, loading]);
  const publicEventBaseUrl = (typeof window !== 'undefined') ? window.location.origin : '';

  // Brief 27 oct E.2 — rendu d'une ligne, partagé par les 3 sections (un seul point de vérité).
  const renderRow = (row) => (
    <SessionCard key={`${row.kind}-${row.booking_id || row.event_id}`} row={row} onAction={handleAction} shareOpenFor={shareOpenFor} setShareOpenFor={setShareOpenFor} publicEventBaseUrl={publicEventBaseUrl} onEditEvent={handleEditEvent} onDeleteEvent={setDeletingRow} onUnbookmark={handleUnbookmark} ownerMenuOpenFor={ownerMenuOpenFor} setOwnerMenuOpenFor={setOwnerMenuOpenFor} onLeaveReview={setReviewingRow} onOpenDetails={setDetailsRow} onPublishEvent={handlePublishEvent} onPayBooking={handlePayBooking} highlightId={highlightId} />
  );

  // Brief Phase B2 §2 — open the edit wizard with the row pre-filled.
  const handleEditEvent = useCallback((row) => {
    // EventCreationWizard expects an event-shaped object. owned_event rows
    // already carry the required fields (event_id, type, title, start_time,
    // end_time, duration, max_capacity, visio_link, etc.).
    setEditingEvent({
      event_id: row.event_id,
      type: row.type,
      title: row.title,
      description: row.description,
      start_time: row.start_time,
      end_time: row.end_time,
      duration_minutes: row.duration,
      max_capacity: row.max_capacity,
      is_free: row.is_free,
      price: row.price,
      currency: row.currency,
      visio_link: row.visio_link,
      visibility: 'public',
      status: row.status || 'published',
    });
  }, []);

  // Brief Phase B2 §2 — confirm + DELETE the session.
  const confirmDeleteEvent = useCallback(async () => {
    if (!deletingRow?.event_id) return;
    try {
      await axios.delete(`${API}/calendar/events/${deletingRow.event_id}`, { withCredentials: true });
      toast.success('Session supprimée');
      setDeletingRow(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Suppression impossible');
    }
  }, [deletingRow, load]);

  // Brief Phase B2 §4 — remove a bookmark.
  const handleUnbookmark = useCallback(async (eventId) => {
    try {
      await axios.delete(`${API}/events/${eventId}/bookmark`, { withCredentials: true });
      toast.success('Retiré de tes événements suivis');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action impossible');
    }
  }, [load]);

  // Brief pré-beta (Task 1) — publier un brouillon.
  const handlePublishEvent = useCallback(async (row) => {
    try {
      await axios.patch(`${API}/calendar/events/${row.event_id}`, { status: 'published' }, { withCredentials: true });
      toast.success('Session publiée ! Tes followers recevront un email groupé sous 30 min.');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Publication impossible');
    }
  }, [load]);

  // Brief pré-beta (Task 4) — régler une réservation en attente de paiement.
  const handlePayBooking = useCallback(async (row) => {
    try {
      const { data } = await axios.post(`${API}/bookings/${row.event_id}/checkout`, {}, { withCredentials: true });
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Paiement impossible');
    }
  }, []);

  // Brief Jun 16 — 2 wizards (Créer une session / Mes disponibilités) + liste schedules
  const [sessionWizardOpen, setSessionWizardOpen] = useState(false);
  const [availabilityWizardOpen, setAvailabilityWizardOpen] = useState(false);
  const [schedules, setSchedules] = useState([]);

  const loadSchedules = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/calendar/availability-schedules`, { withCredentials: true });
      setSchedules(data?.schedules || []);
    } catch (e) {
      // silent — availability schedules are optional
    }
  }, []);

  useEffect(() => { if (user?.user_id) loadSchedules(); }, [user?.user_id, loadSchedules]);

  const toggleSchedule = useCallback(async (eventId, makeActive) => {
    try {
      await axios.patch(`${API}/calendar/events/${eventId}`, {
        status: makeActive ? 'published' : 'cancelled',
      }, { withCredentials: true });
      toast.success(makeActive ? 'Disponibilité réactivée' : 'Disponibilité désactivée');
      loadSchedules();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action impossible');
    }
  }, [loadSchedules, load]);

  return (
    <div className="space-y-6" data-testid="my-sessions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Mes sessions</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Toutes tes sessions — en tant qu&apos;expert et en tant qu&apos;apprenant.
          </p>
        </div>
        {/* Brief Jun 16 §1 — 2 boutons : Créer une session + Mes disponibilités */}
        <div className="flex items-center gap-2 self-start sm:self-end" data-testid="mysessions-action-buttons">
          <button
            type="button"
            onClick={() => setSessionWizardOpen(true)}
            data-testid="mysessions-create-session-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white shadow hover:opacity-90 transition-opacity"
            style={{ backgroundColor: '#7F77DD' }}
          >
            <CalendarPlus className="h-4 w-4" /> Créer une session
          </button>
          <button
            type="button"
            onClick={() => setAvailabilityWizardOpen(true)}
            data-testid="mysessions-availability-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white shadow hover:opacity-90 transition-opacity"
            style={{ backgroundColor: '#1D9E75' }}
          >
            <Clock className="h-4 w-4" /> Mes disponibilités
          </button>
        </div>
      </div>

      {/* Brief Jul 10 2026 — Intégration Prospection : onglets internes.
          Brief Sep 2026 — le widget revenus est remplacé par l'onglet Revenus. */}
      <div className="flex items-center gap-1 border-b border-gray-200 dark:border-gray-700" data-testid="mysessions-tabs">
        {[
          { id: 'sessions', label: 'Sessions', icon: Calendar },
          { id: 'prospection', label: 'Prospection', icon: TrendingUp },
          { id: 'revenus', label: 'Revenus', icon: Banknote },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => switchTab(id)}
            data-testid={`mysessions-tab-${id}`}
            className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              activeTab === id
                ? 'border-[#7F77DD] text-[#7F77DD]'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
            }`}
          >
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {activeTab === 'prospection' && (
        <div data-testid="mysessions-prospection-tab">
          <ProspectionDashboard />
        </div>
      )}

      {activeTab === 'revenus' && (
        <div data-testid="mysessions-revenus-tab">
          <RevenueTab />
        </div>
      )}

      {activeTab === 'sessions' && (
        <>
      {/* Always-on expert calendar — Sprint 1.0c V1.a-bis (Bug 6 fix v2) :
          flow normal flex, calendrier en pixels explicites pour éviter tout overlap
          react-big-calendar (qui utilise position:absolute en interne). */}
      <div
        className="rounded-2xl bg-gradient-to-br from-[#0d1f35] via-[#0f2540] to-[#0d1f35] p-3 sm:p-5 shadow-xl"
        data-testid="mysessions-expert-calendar-fixed"
      >
        <ExpertCalendar ownerUserId={user?.user_id} compact hideNewButton />
      </div>

      {/* Brief Jun 16 §5 — Mes disponibilités section (under calendar) */}
      {schedules.length > 0 && (
        <section data-testid="mysessions-schedules-section" className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Mes disponibilités ({schedules.length})</h2>
          </div>
          <div className="space-y-2">
            {schedules.map(sched => {
              const days = (sched.recurrence?.days_of_week || []).map(d => ['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'][d]).join(', ');
              const active = sched.status === 'published';
              return (
                <div
                  key={sched.event_id}
                  data-testid={`mysessions-schedule-${sched.event_id}`}
                  className={`flex items-center gap-3 p-3 rounded-xl border ${
                    active
                      ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                      : 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 opacity-60'
                  }`}
                >
                  <div className="h-9 w-9 rounded-full flex items-center justify-center" style={{ backgroundColor: '#1D9E75' + '22' }}>
                    <Clock className="h-4 w-4" style={{ color: '#1D9E75' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{sched.title}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {days || '—'} · créneaux de {sched.slot_duration_minutes || sched.duration_minutes || 60} min
                      {sched.slot_buffer_minutes ? ` · tampon ${sched.slot_buffer_minutes} min` : ''}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleSchedule(sched.event_id, !active)}
                    data-testid={`mysessions-schedule-toggle-${sched.event_id}`}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium ${
                      active
                        ? 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                        : 'bg-[#1D9E75] text-white hover:opacity-90'
                    }`}
                  >
                    {active ? (<><PowerOff className="h-3 w-3" /> Désactiver</>) : (<><Power className="h-3 w-3" /> Réactiver</>)}
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Brief Jun 15 (post Vague 2) §3 — banner pendingInvitations en haut
          de la liste (sous le calendrier, au-dessus des onglets). */}
      {pendingInvitations > 0 && (
        <div
          className="flex items-center gap-3 px-3.5 py-2.5 rounded-md"
          style={{ background: '#EEEDFE', color: '#3C3489', borderRadius: 'var(--border-radius-md)' }}
          data-testid="mysessions-pending-banner"
        >
          <Bell className="h-4 w-4 shrink-0" />
          <p className="flex-1 text-sm font-medium">
            Tu as <strong>{pendingInvitations}</strong> invitation{pendingInvitations > 1 ? 's' : ''} en attente de ta réponse
          </p>
          <button
            type="button"
            onClick={() => {
              setFilter('all');
              setSearchParams({ filter: 'pending' });
              setTimeout(() => {
                const el = document.querySelector('[data-testid="mysessions-pending-section"]');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 100);
            }}
            className="text-sm font-semibold underline hover:opacity-80"
            data-testid="mysessions-pending-banner-cta"
          >
            Voir →
          </button>
        </div>
      )}

      {/* Filter pills — Brief Vague 2 §2 : remplacé par [Tout] [Invitations] uniquement
          (les rôles Expert/Apprenant sont maintenant affichés en badge sur chaque item). */}
      <div className="flex flex-wrap items-center gap-2" data-testid="mysessions-filter-pills">
        {[
          { id: 'all', label: 'Tout', count: counts.all },
          { id: 'invitation', label: 'Invitations', count: counts.invitation, icon: Mail },
        ].map(({ id, label, count, icon: Icon }) => (
          <button
            key={id}
            data-testid={`mysessions-filter-${id}`}
            onClick={() => setFilter(id)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
              filter === id
                ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white border-transparent shadow'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700 hover:border-purple-300'
            }`}
          >
            {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
            {label}
            <span className={`ml-1 text-xs ${filter === id ? 'text-white/80' : 'text-gray-400'}`}>{count}</span>
          </button>
        ))}
      </div>

      {/* List zone — normal flow under the calendar (no max-height trickery). */}
      {/* Brief Jul 7 2026 §4 — demandes de session 1-to-1 spontanées */}
      <SessionRequestsSection onChanged={load} />
      <InterestedSection filterActive={searchParams.get('filter') === 'interested'} />
      <div className="space-y-6" data-testid="mysessions-list-scroll">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-500">
            <Loader2 className="h-6 w-6 animate-spin mr-2" /> Chargement...
          </div>
        ) : enrichedSessions.length === 0 ? (
          <div className="p-10 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 text-center text-gray-500 dark:text-gray-400">
            <Inbox className="h-8 w-8 mx-auto mb-3 opacity-50" />
            <p className="font-medium text-gray-700 dark:text-gray-200">Aucune session pour l&apos;instant</p>
            <p className="text-sm mt-1">Créez un événement dans le calendrier ci-dessus ou réservez une session avec un expert.</p>
            <button
              data-testid="discover-experts-btn"
              onClick={() => navigate('/app/discover')}
              className="mt-4 px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium text-sm"
            >
              Découvrir des experts →
            </button>
          </div>
        ) : (
          <>
            {pending.length > 0 && (
              <SessionSection testId="mysessions-pending-section" title="En attente" rows={pending} highlightId={highlightId} defaultExpanded={searchParams.get('filter') === 'pending'} renderRow={renderRow} />
            )}

            <SessionSection testId="sessions-upcoming-section" title="À venir" rows={upcoming} highlightId={highlightId} renderRow={renderRow}
              empty={(
                <div className="p-6 rounded-xl border border-dashed border-gray-300 dark:border-gray-700 text-center text-sm text-gray-500 dark:text-gray-400">
                  <UserIcon className="h-5 w-5 mx-auto mb-2 opacity-50" />
                  Aucune session à venir.
                </div>
              )}
            />

            {past.length > 0 && (
              <SessionSection testId="sessions-past-section" title="Passées" rows={past.slice(0, 20)} highlightId={highlightId} renderRow={renderRow} dimmed />
            )}
          </>
        )}
      </div>
        </>
      )}

      {/* Brief Jul 7 2026 §1 — confirmation post-paiement */}
      <PaymentSuccessModal
        info={paymentSuccess}
        onClose={() => setPaymentSuccess(null)}
        onViewSession={() => {
          const id = paymentSuccess?.booking_id;
          setPaymentSuccess(null);
          if (id) {
            setHighlightId(String(id));
            setTimeout(() => {
              document.querySelector(`[data-testid="session-row-${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 300);
            setTimeout(() => setHighlightId(null), 4000);
          }
        }}
      />
      {/* Brief Jun 16 §1+§2+§3 — wizards modals */}
      <SessionDetailsDrawer
        row={detailsRow}
        isOpen={!!detailsRow}
        onClose={() => setDetailsRow(null)}
        currentUserId={user?.user_id}
        onAction={(kind, id, action) => { setDetailsRow(null); handleAction(kind, id, action); }}
      />
      <SessionWizard
        isOpen={sessionWizardOpen}
        onClose={() => setSessionWizardOpen(false)}
        onCreated={() => { load(); loadSchedules(); }}
      />
      <AvailabilityWizard
        isOpen={availabilityWizardOpen}
        onClose={() => setAvailabilityWizardOpen(false)}
        onCreated={() => { load(); loadSchedules(); }}
      />

      {/* Brief Phase B2 §2 — edit wizard for owned sessions in the list. */}
      <EventCreationWizard
        isOpen={!!editingEvent}
        editEvent={editingEvent}
        onClose={() => { setEditingEvent(null); load(); loadSchedules(); }}
      />

      {/* Brief Phase B2 §2 — confirm delete dialog. */}
      {deletingRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="session-delete-confirm">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDeletingRow(null)} />
          <div className="relative w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-2xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Supprimer cette session ?</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1.5">
              « <strong>{deletingRow.title}</strong> » sera annulée. Les éventuels inscrits seront informés. Cette action est irréversible.
            </p>
            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={() => setDeletingRow(null)}
                className="px-4 py-2 rounded-lg text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                data-testid="session-delete-cancel"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={confirmDeleteEvent}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm bg-red-500 hover:bg-red-600 text-white font-medium"
                data-testid="session-delete-confirm-btn"
              >
                <Trash2 className="h-3.5 w-3.5" /> Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Brief Phase B3 §1 — Modal "Laisser un avis" sur une session passée. */}
      {reviewingRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="session-review-modal">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setReviewingRow(null)} />
          <div className="relative w-full max-w-lg">
            <ReviewForm
              bookingId={reviewingRow.booking_id}
              targetName={reviewingRow.counterpart?.display_name || 'cet expert'}
              onSuccess={() => {
                toast.success('Merci pour ton avis !');
                setReviewingRow(null);
                load();
              }}
              onCancel={() => setReviewingRow(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default MySessions;
